import { useRef, useState } from 'react'
import { Typography, styled } from '@mui/material'
import { useToggle } from 'usehooks-ts'
import { useHistoryCustomBack } from 'weblite-router'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { t } from '$i18n'
import { Label } from '$common/label'
import { Switch } from '$common/switch'
import { TextInput } from '$common/input'
import { BackButton } from '$common/buttons'
import { nothing } from '$helper/functions/utils'
import { BottomDrawer } from '$common/bottom-drawer'
import { SecondaryButton } from '$common/buttons/secondary-button'
import { SelectDateTime } from '$common/select-date-time/select-date-time'
import { getDateOfNDaysLater } from '$comp/modals/date-picker/date-picker.helper'
import { ErrorOutlinedButton } from '$common/buttons/error-outlined-button'
import { ChannelCallType } from '../choose-call-type/choose-channel-call'
import { GroupCallType } from '../choose-call-type/choose-group-call'
import { dateWithOffset } from '$setup/time-offset'

export type HallCallType = ChannelCallType | GroupCallType
const MINUTE_IN_MS = 60 * 1000

export interface HallCallTime {
  time: Date
  shouldSendNotificationMessage: boolean
  title: string
  shouldStartNow: boolean
}

function isDateNow(date: Date) {
  const now = dateWithOffset()
  return +date - +now < MINUTE_IN_MS
}

export interface Props {
  isOpen?: boolean
  callType: HallCallType
  onConfirm: (hallCallTime: HallCallTime) => unknown
  onCancel: () => unknown
  hallType: 'CHANNEL' | 'GROUP'
  defaultTitle?: string
  defaultShouldSendNotification?: boolean
  onBack?: () => void
  editMode?: boolean
  defaultTime?: Date
  onDelete?: () => unknown
}

export function ChooseHallCallTime({
  isOpen = false,
  onConfirm,
  onCancel,
  callType,
  hallType,
  defaultTitle = '',
  defaultTime,
  defaultShouldSendNotification = false,
  onBack = closeCurrentModal,
  editMode = false,
  onDelete = nothing,
}: Props) {
  const startDateRef = useRef(new Date())
  const [time, setTime] = useState(defaultTime || startDateRef.current)
  const [shouldSendNotification, toggleShouldSendNotification] = useToggle(
    defaultShouldSendNotification,
  )
  const [title, setTitle] = useState(defaultTitle)

  const shouldSendNotificationLabel =
    hallType === 'CHANNEL'
      ? t('chooseHallCallTimeModal.sendNotificationInChannel')
      : t('chooseHallCallTimeModal.sendNotificationInGroup')

  const dialogTitle = t(
    `chooseHallCallTimeModal.${editMode ? 'editingCall' : 'creatingCall'}.${
      callType === 'tribune'
        ? 'tribune'
        : callType === 'group_call'
          ? 'meeting'
          : 'live'
    }`,
  )

  useHistoryCustomBack(
    () => {
      onBack()
    },
    [onBack],
    Boolean(onBack),
  )

  return (
    <BottomDrawer isOpen={Boolean(isOpen)} onClose={onCancel}>
      <Root>
        <TitleContainer>
          <BackButton onClick={onBack} color="secondary700" />
          <Typography variant="subtitle1" color="secondary700">
            {dialogTitle}
          </Typography>
        </TitleContainer>

        <FormContainer>
          <Label
            typographyProps={{ variant: 'subtitle3' }}
            title={t('chooseHallCallTimeModal.title')}
          />
          <TextInput
            value={title}
            onChange={setTitle}
            placeholder={t('chooseHallCallTimeModal.titlePlaceholder')}
            rootSx={{ mb: '24px' }}
          />
          <SelectDateTime
            labelProps={{
              typographyProps: {
                variant: 'subtitle3',
              },
            }}
            value={time}
            startDate={startDateRef.current}
            endDate={getDateOfNDaysLater(7)}
            label={t('chooseHallCallTimeModal.startTime')}
            onSelect={setTime}
            drawerTitle={t('chooseHallCallTimeModal.selectStartTime')}
            selectButtonSx={{ mb: '24px' }}
          />
          {/* {!editMode && ( */}
          <RadioButtonContainer onClick={toggleShouldSendNotification}>
            <Typography variant="body2" color="neutral900">
              {shouldSendNotificationLabel}
            </Typography>
            <Switch checked={shouldSendNotification} />
          </RadioButtonContainer>
          {/* )} */}
        </FormContainer>

        <ButtonsContainer>
          {editMode ? (
            <>
              <ErrorOutlinedButton
                text={t('common.remove')}
                sx={{ height: '48px', width: 'calc(50% - 8px)' }}
                onClick={onDelete}
              />
              <SecondaryButton
                text={t('common.save')}
                sx={{ height: '48px', width: 'calc(50% - 8px)' }}
                onClick={() =>
                  onConfirm({
                    time,
                    shouldSendNotificationMessage: shouldSendNotification,
                    title,
                    shouldStartNow: isDateNow(time),
                  })
                }
              />
            </>
          ) : (
            <SecondaryButton
              fullWidth
              text={t('common.ok')}
              sx={{ height: '48px' }}
              onClick={() =>
                onConfirm({
                  time,
                  shouldSendNotificationMessage: shouldSendNotification,
                  title,
                  shouldStartNow: isDateNow(time),
                })
              }
            />
          )}
        </ButtonsContainer>
      </Root>
    </BottomDrawer>
  )
}

const Root = styled('div')`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 16px;
`

const TitleContainer = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: -8px;
  margin-bottom: 20px;
`

const FormContainer = styled('div')`
  display: flex;
  flex-direction: column;
`

const RadioButtonContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 0;
  margin-bottom: 24px;
`

const ButtonsContainer = styled('div')`
  width: 100%;
  display: flex;
  gap: 8px;
`
